import { Grid } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { queryParams } from 'utils/query';
import { MoreFiltersDrawer } from 'components/Table/Filters/MoreFiltersDrawer';
import { ActiveFiltersBar } from 'components/Table/Filters/ActiveFiltersBar';
import { TableConfig } from 'models/general';
import { WorklistStatus } from 'models/worklist';
import { PriorityIcon } from 'components/PriorityIcon';
import { InfoIcon } from 'components/InfoIcon';
import { DebouncedInput } from 'components/ControlledInputs/DebouncedInput';
import { PortalButton } from 'components/Button/PortalButton';
import { getPriorityInfo } from 'utils/worklist';
import { Priority, PriorityKeys } from 'models/rules';
import { useAppContext } from 'context/hooks';
import { FilterGroups } from './FilterGroups';
import { FilterStatus } from './FilterStatus';
import { FilterDates } from './FilterDates';
import { FilterCheckList } from './FilterCheckList';
import { useBadges } from './WorklistBadges';

interface Props {
  config: TableConfig;
}

export const WorkListFilters = ({ config }: Props) => {
  const { queryKey } = config;
  const { t } = useTranslation();
  const { parameters: filters, setParameters, resetParameters } = useSearchParameters(queryKey);
  const { updateTableRowIds } = useAppContext();

  const handleFilterChange = (filterKey: string, input: string) => {
    setParameters({ ...filters, [filterKey]: input });
  };

  const handleResetAction = () => {
    resetParameters();
    updateTableRowIds?.([]);
  };

  const statusChoices = Object.values(WorklistStatus).map((status: WorklistStatus) => ({
    value: status,
    label: `${t('status_table_heading')}: ${t(`label_status_${status.toLowerCase()}`)}`,
  }));

  const priorityChoices = Object.values(Priority).map((priority: PriorityKeys) => ({
    value: getPriorityInfo(priority).filter,
    label: <PriorityIcon priority={priority} />,
  }));

  return (
    <Grid p={'xs'} align={'center'}>
      <Grid.Col span={2.25}>
        <DebouncedInput
          searchOption
          onChange={(value) => handleFilterChange(queryParams.title, value)}
          value={filters[queryParams.title]}
          placeholder={t('search_by_alert_title')}
        />
      </Grid.Col>
      <Grid.Col span={2.25}>
        <FilterGroups queryKey={queryKey} />
      </Grid.Col>
      <Grid.Col span={2}>
        <FilterStatus choices={statusChoices} config={config} />
      </Grid.Col>
      <Grid.Col span={1}>
        <MoreFiltersDrawer queryKey={queryKey}>
          <Grid>
            <Grid.Col span={6}>
              <FilterCheckList
                filterField={queryParams.priority}
                title={t('priority_label')}
                queryKey={queryKey}
                choices={priorityChoices}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <FilterDates title={t('timestamp_label')} queryKey={queryKey} />
            </Grid.Col>
          </Grid>
        </MoreFiltersDrawer>
      </Grid.Col>
      <Grid.Col span={3.25}>
        <ActiveFiltersBar config={config} badges={useBadges()} datesKey={'timestamp_label'} />
      </Grid.Col>
      <Grid.Col span={1} ta={'right'}>
        <PortalButton onClick={handleResetAction} label={t('reset_filters')} variant={'outline'} />
      </Grid.Col>
      <Grid.Col span={0.25} ta={'right'}>
        <InfoIcon i18nKey={'worklist_tooltip'} />
      </Grid.Col>
    </Grid>
  );
};
